import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    defaultInputPadding,
    TextInput,
    TextInputProps,
} from 'components/forms/TextInput';
import { forwardRef, Ref } from 'react';
import styled, { css } from 'styled-components';
import { tokens } from 'styles/tokens';
import { PickForStyled } from 'utils/types';

const Wrapper = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

type SearchBoxProps = PickForStyled<
    SearchInputProps,
    'background' | 'isRounded' | 'inputSize' | 'showSearchClearButton' | 'bordered'
>;

const SearchBox = styled(TextInput)<SearchBoxProps>`
    width: 100%;
    padding-left: ${(props) => props.padding ?? defaultInputPadding};
    ${(props) =>
        props.$background === 'transparent'
            ? 'background-color: transparent'
            : props.$background === 'inputBackground'
              ? `background-color: ${tokens.input.background};`
              : css`
                  background-color: ${tokens.background.paperElevation2};
              `};

    /* account for the search icon, so text does not overlap */
    padding-right: ${(props) => (props.$inputSize === 'large' ? '3.5rem' : '3rem')};

    ${({ $showSearchClearButton }) =>
        !$showSearchClearButton &&
        css`
            &::-webkit-search-cancel-button {
                /* Remove default */
                appearance: none;

                /* hide */
                display: none;
            }
        `}

    border-radius: ${(props) => (props.$isRounded ? '50px' : '3px')};

    ${({ $bordered }) =>
        $bordered &&
        css`
            border: 1px solid ${tokens.input.border};
        `}

    &:disabled {
        color: ${tokens.text.disabled};
        cursor: not-allowed;
    }
`;

const SearchIcon = styled.div<{ $inputSize: 'small' | 'medium' | 'large' }>`
    display: inline-block;
    position: absolute;
    right: ${(props) => (props.$inputSize === 'large' ? '1.5rem' : '1rem')};
    color: ${tokens.text.primary};
`;

export type SearchInputProps = Omit<TextInputProps, 'type' | 'padding'> & {
    /**
     * Whether or not to show the "X" clear button that clears the text in the field.
     */
    showSearchClearButton?: boolean;
    /**
     * Size of the search input. Defaults to `medium`.
     */
    inputSize?: 'small' | 'medium' | 'large';
    /**
     * If true, search box will have rounded edges
     */
    isRounded?: boolean;
    /**
     * The background to apply to the search input. Defaults to `backgroundColor`.
     */
    background?: 'transparent' | 'backgroundColor' | 'inputBackground';
    /**
     * Whether to add a border to the search input.
     */
    bordered?: boolean;
};

const getPadding = (inputSize: 'small' | 'medium' | 'large') => {
    switch (inputSize) {
        case 'small':
            return '0.5rem';
        case 'medium':
            return '1rem';
        case 'large':
            return '1.5rem';
    }
};

/**
 * The `search`-type specific version of TextInput with appropriate styles added.
 */
function SearchInput(
    {
        showSearchClearButton,
        inputSize,
        isRounded = false,
        background = 'backgroundColor',
        bordered = false,
        ...rest
    }: SearchInputProps,
    ref: Ref<HTMLInputElement>,
) {
    return (
        <Wrapper>
            <SearchBox
                ref={ref}
                type="search"
                $showSearchClearButton={showSearchClearButton ?? false}
                $inputSize={inputSize ?? 'medium'}
                $isRounded={isRounded}
                $background={background}
                $bordered={bordered}
                padding={getPadding(inputSize ?? 'medium')}
                data-cy="search-input"
                {...rest}
            />
            <SearchIcon $inputSize={inputSize ?? 'medium'}>
                <FontAwesomeIcon icon={faSearch} />
            </SearchIcon>
        </Wrapper>
    );
}

const forwardRefSearchInput = forwardRef(SearchInput);

export { forwardRefSearchInput as SearchInput };
